import { useState, useEffect } from "react";
import {
  Card,
  CardHeader,
  Typography,
  Button,
  CardBody,
  CardFooter,
  IconButton,
  Input,
  Dialog,
  DialogHeader,
  DialogBody,
  DialogFooter,
  Checkbox,
  Select as MTSelect,
  Option,
} from "@material-tailwind/react";
import { FormControl, TextField, Badge } from "@mui/material";
import { HiMiniPencil } from "react-icons/hi2";
import { IoPersonCircle, IoClose } from "react-icons/io5";
import Paper from "@mui/material/Paper";
import { DataGrid } from "@mui/x-data-grid";
import * as XLSX from "xlsx";
import FilterListIcon from "@mui/icons-material/FilterList";
import DownloadIcon from "@mui/icons-material/Download";

import { useContactsContext } from "../../contexts/ContactsContextProvider";
import { useUserContext } from "../../contexts/UserContextProvider";
import {
  isoTimestampToLocal,
  getCurrentDateTime,
  isoTimestampToLocalReadable,
} from "../utils/timeStampConversion";
import { phoneNumberSanitation } from "../utils/inputSanitation";
import { CustomLoader, CreateContactGroup } from "..";
import { defaultSearchData } from "../../data/filterData";
import { toast } from "react-toastify";

const ManageAllContacts = ({
  drawerStatus,
  callbackHideAllContacts,
  groupName = "",
  contactInGroup = [],
  tableDialogSize = "80vh",
}) => {
  const {
    totalPages,
    contactGroups,
    apiCallFetchUserContacts,
    apiCallFetchTablePaging,
    apiCallAssignContactGroups,
    apiCallDeleteSelectedContacts,
    apiCallDownloadAllContacts,
    apiCallUpdateContact,
    isLoading,
    apiCallFetchContactGroups,
  } = useContactsContext();

  const {
    currentUserInfo,
    apiCallGetUserAgents,
    userAgentsList,
    isUserAdminOrSuperadmin,
  } = useUserContext();
  const [currentPage, setCurrentPage] = useState(1);
  const [searchContact, setSearchContact] = useState(defaultSearchData);
  const [toEditContact, setToEditContact] = useState({});
  const [deleteContactId, setContactForDelete] = useState([]);
  const [openEditContact, setOpenEditContact] = useState(false);
  const [targetContactGroup, setTargetContactGroup] = useState("");
  const [contactsSelected, setContactsSelected] = useState([]);
  const [filterContact, setFilterContact] = useState([]);
  const [totalSelectedCount, setTotalSelectedCount] = useState(0);
  const [selectAll, setSelectAll] = useState(false);
  const [contactPageSize, setContactPageSize] = useState("100");
  const [openGroupCreationDialog, setOpenGroupCreationDialog] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [notInterestedContact, setNotInterestedContact] = useState(0);
  const [showFilter, setShowFilter] = useState(false);
  const [hasFilter, setHasFilter] = useState(null);
  const [updatedUserList, setUpdatedUserList] = useState([]);

  const pageSizeArray = ["50", "100"];

  const resetStates = () => {
    setCurrentPage(1);
    setSearchContact(defaultSearchData);
    setToEditContact({});
    setContactForDelete([]);
    setOpenEditContact(false);
    setTargetContactGroup("");
    setContactsSelected([]);
    setContactPageSize("50");
    setSelectAll(false);
    setTotalSelectedCount(0);
  };

  const contactsColumns = [
    {
      field: "select",
      headerName: "",
      width: 50,
      sortable: false,
      disableColumnMenu: true,
      renderHeader: () => (
        <Checkbox
          checked={selectAll}
          indeterminate={
            contactsSelected.length > 0 &&
            contactsSelected.length < filterContact?.length
          }
          onChange={handleSelectAllOnChange}
          title="Select All"
        />
      ),
      renderCell: (params) => (
        <Checkbox
          checked={contactsSelected.includes(
            JSON.stringify({
              countryCode: params?.row?.countryCode,
              phoneNo: params?.row?.phoneNo,
            })
          )}
          onChange={(e) =>
            selectMultipleContactsOnClick(
              e,
              JSON.stringify({
                countryCode: params?.row?.countryCode,
                phoneNo: params?.row?.phoneNo,
              })
            )
          }
        />
      ),
    },
    {
      field: "index",
      headerName: "S.No",
      width: 70,
      sortable: false,
      valueGetter: (value, row) => {
        const index = filterContact?.findIndex(
          (contact) => contact?.contactId === row?.contactId
        );
        return index + 1;
      },
    },
    {
      field: "name",
      headerName: "Name",
      width: 400,
      sortable: true,
      valueGetter: (value, row) =>
        `${row?.firstName || ""} ${row?.lastName || ""}`.trim(),
      renderCell: (params) => (
        <div className="flex items-center gap-3">
          <IoPersonCircle size={20} className="text-slate-800" />
          <span className="font-bold">{params.value || "-"}</span>
        </div>
      ),
    },
    {
      field: "phone",
      headerName: "Phone",
      width: 200,
      sortable: true,
      valueGetter: (value, row) => `+${row?.countryCode} ${row?.phoneNo}`,
    },
    {
      field: "lastContacted",
      headerName: "Last Contacted",
      width: 200,
      sortable: true,
      valueGetter: (value, row) =>
        row?.lastContacted
          ? isoTimestampToLocalReadable(row.lastContacted)
          : "-",
    },
    {
      field: "createdOn",
      headerName: "Created On",
      width: 200,
      sortable: true,
      valueGetter: (value, row) =>
        row?.createdOn ? isoTimestampToLocalReadable(row.createdOn) : "-",
    },
    {
      field: "email",
      headerName: "Email",
      width: 300,
      sortable: true,
      valueGetter: (value, row) => row?.email || "-",
    },
    {
      field: "isVerifiedNumber",
      headerName: "Is Verified No.?",
      width: 150,
      sortable: true,
      valueGetter: (value, row) => {
        if (row?.isVerifiedNumber === 1) return "Yes";
        else if (row?.isVerifiedNumber === 0) return "No";
        return "-";
      },
    },
    {
      field: "waOptIn",
      headerName: "WA Opt In?",
      width: 150,
      sortable: true,
      valueGetter: (value, row) => {
        if (row?.waOptIn === 2) return "Yes";
        else if (row?.waOptIn === 1) return "Yes";
        else if (row?.waOptIn === 0) return "No";
        return "-";
      },
    },
    {
      field: "isBlocked",
      headerName: "Is Blocked?",
      width: 150,
      sortable: true,
      valueGetter: (value, row) => {
        if (row?.isBlocked === 1) return "Yes";
        if (row?.isBlocked === 0) return "No";
        return "-";
      },
    },
    {
      field: "companyName",
      headerName: "Company",
      width: 200,
      sortable: true,
      valueGetter: (value, row) => row?.companyName || "-",
    },
    {
      field: "remarks",
      headerName: "Remarks",
      width: 250,
      sortable: true,
      valueGetter: (value, row) => row?.remarks || "-",
    },
    {
      field: "createdBy",
      headerName: "Created By",
      width: 150,
      sortable: true,
      valueGetter: (value, row) => row?.createdBy || "-",
    },
    {
      field: "lastContactedBy",
      headerName: "Last Contacted By",
      width: 180,
      sortable: true,
      valueGetter: (value, row) => row?.lastContactedBy || "-",
    },
    {
      field: "actions",
      headerName: "Actions",
      width: 100,
      sortable: false,
      disableColumnMenu: true,
      renderCell: (params) => (
        <button
          title="Edit Contact"
          onClick={() => {
            setOpenEditContact(!openEditContact);
            setToEditContact(params.row);
            setContactForDelete([
              {
                countryCode: params.row?.countryCode,
                phoneNo: params.row?.phoneNo,
              },
            ]);
          }}
        >
          <IconButton variant="text">
            <HiMiniPencil className="h-4 w-4" />
          </IconButton>
        </button>
      ),
    },
  ];

  const handleDateChange = (e) => {
    const { name, value } = e.target;
    setSearchContact((prevData) => ({
      ...prevData,
      [name]: value,
    }));
  };

  const handleChange = (field) => (event) => {
    if (
      field === "waOptIn" ||
      field === "isVerifiedNo" ||
      field === "isBlocked" ||
      field === "createdBy" ||
      field === "lastContactedBy"
    ) {
      setSearchContact({
        ...searchContact,
        [field]: event ?? event.target.checked,
      });
    } else {
      // Direct value from MTSelect
      setSearchContact({
        ...searchContact,
        [field]: event.target.value ?? event.target.checked,
      });
    }
  };

  const getContactsOnClick = async (page) => {
    setCurrentPage(page);
    const contact = await apiCallFetchUserContacts(
      page,
      searchContact,
      contactPageSize
    );
    setFilterContact(contact?.contacts);
    setNotInterestedContact(contact?.contactSummary?.notInterested);
    apiCallFetchTablePaging(searchContact, contactPageSize);
    setSelectAll(false);
  };

  const loadContactInGroup = async () => {
    if (groupName && groupName !== "") {
      let includeContact = [];
      // eslint-disable-next-line array-callback-return
      contactInGroup?.map((contact) => {
        includeContact.push(contact?.contactId);
      });
      const filterData = await apiCallFetchUserContacts(
        1,
        searchContact,
        contactPageSize,
        includeContact.join(",")
      );
      setFilterContact(filterData?.contacts);
      setNotInterestedContact(filterData?.contactSummary?.notInterested);
      apiCallFetchTablePaging(searchContact, contactPageSize);
      setTotalSelectedCount(0);
      setSelectAll(false);
      setContactsSelected([]);
      setTargetContactGroup(groupName);
    } else {
      const filterData = await apiCallFetchUserContacts(
        1,
        searchContact,
        contactPageSize,
        ""
      );
      setFilterContact(filterData?.contacts);
      setNotInterestedContact(filterData?.contactSummary?.notInterested);
    }
  };

  const handlePrevious = async () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
      const contact = await apiCallFetchUserContacts(
        currentPage - 1,
        searchContact,
        contactPageSize
      );
      setFilterContact(contact?.contacts);
      setNotInterestedContact(contact?.contactSummary?.notInterested);
      setSelectAll(false);
    }
  };

  const handleNext = async () => {
    if (currentPage < totalPages.length) {
      setCurrentPage(currentPage + 1);
      const contact = await apiCallFetchUserContacts(
        currentPage + 1,
        searchContact,
        contactPageSize
      );
      setFilterContact(contact?.contacts);
      setNotInterestedContact(contact?.contactSummary?.notInterested);
      setSelectAll(false);
    }
  };

  const getVisiblePages = () => {
    const start = Math.floor((currentPage - 1) / 5) * 5 + 1;
    const end = Math.min(start + 4, totalPages.length);
    return Array.from({ length: end - start + 1 }, (_, i) => start + i);
  };

  const selectMultipleContactsOnClick = (event, selectedData) => {
    const isChecked = event.target.checked;
    setContactsSelected((prevSelectedContacts) => {
      const updatedSelectedContacts = isChecked
        ? [...prevSelectedContacts, selectedData]
        : prevSelectedContacts.filter((data) => data !== selectedData);
      setTotalSelectedCount(updatedSelectedContacts?.length);
      setSelectAll(updatedSelectedContacts.length === filterContact.length);
      return updatedSelectedContacts;
    });
  };

  const assignContactGroupsOnSubmit = async (event) => {
    event.preventDefault();

    if (contactGroups.filter((x) => x.groupName === targetContactGroup)) {
      const result = await apiCallAssignContactGroups(
        contactsSelected,
        targetContactGroup
      );

      if (result?.success) {
        callbackHideAllContacts();
        resetStates();
      }
    }
  };

  const deleteSelectedContactsOnClick = (event) => {
    event.preventDefault();
    apiCallDeleteSelectedContacts(deleteContactId);
    setOpenEditContact(!openEditContact);
    getContactsOnClick(currentPage);
  };

  const getContactsOnSubmit = (event) => {
    event.preventDefault();

    const isApplied = Object.keys(searchContact).some(
      (key) => searchContact[key] !== defaultSearchData[key]
    );
    setHasFilter(isApplied);
    setCurrentPage(1);
    getContactsOnClick(currentPage);
  };

  const handleContactEditOnChange = (e) => {
    let { name, value } = e.target;

    if (name === "phoneNo" || name === "countryCode") {
      value = phoneNumberSanitation(value);
    }

    setToEditContact((prevState) => ({
      ...prevState,
      [name]: value,
    }));
  };

  const handleContactEditOnSubmit = async (e) => {
    e.preventDefault();

    const response = await apiCallUpdateContact({
      contactId: toEditContact?.contactId,
      firstName: toEditContact?.firstName,
      lastName: toEditContact?.lastName,
      email: toEditContact?.email,
      waOptIn: toEditContact?.waOptIn,
      isBlocked: toEditContact?.isBlocked,
      remarks: toEditContact?.remarks,
      companyName: toEditContact?.companyName,
      city: toEditContact?.city,
      country: toEditContact?.country,
      countryCode: toEditContact?.countryCode,
      phoneNumber: toEditContact?.phoneNo,
    });

    if (response) {
      setSearchContact(defaultSearchData);
      setOpenEditContact(!openEditContact);
      setFilterContact((prevContacts) =>
        prevContacts.map((contact) =>
          contact.contactId === toEditContact?.contactId
            ? { ...contact, ...toEditContact }
            : contact
        )
      );
    }
  };

  const downloadAllContactsOnClick = async () => {
    const allContactsForDownload = await apiCallDownloadAllContacts();

    if (allContactsForDownload?.data) {
      console.log("allContactsForDownload", allContactsForDownload);
      const worksheet = XLSX.utils.json_to_sheet(allContactsForDownload?.data);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "Sheet1");
      XLSX.writeFile(workbook, `All Contacts ${getCurrentDateTime()}.xlsx`);
    } else {
      toast("No contacts found to be downloaded");
    }
  };

  const handleSelectAllOnChange = (event) => {
    const isChecked = event.target.checked;
    setSelectAll(isChecked);
    const selectedContact = isChecked
      ? filterContact.map((contact) => {
          return JSON.stringify({
            countryCode: contact?.countryCode,
            phoneNo: contact?.phoneNo,
          });
        })
      : [];
    setContactsSelected(selectedContact);
    setTotalSelectedCount(selectedContact?.length);
  };

  const handleSelectedContactDelete = async (event) => {
    event.preventDefault();

    apiCallDeleteSelectedContacts(contactsSelected);
    setCurrentPage(1);

    const contact = await apiCallFetchUserContacts(
      currentPage,
      searchContact,
      contactPageSize
    );

    setFilterContact(contact?.contacts);
    setNotInterestedContact(contact?.contactSummary?.notInterested);
    setTotalSelectedCount(0);
    setSelectAll(false);
    setContactsSelected([]);
    callbackHideAllContacts();
    setOpenDeleteDialog(false);
  };

  useEffect(() => {
    if (userAgentsList.length > 0) {
      const modifiedList = [{ agentUsername: "all" }, ...userAgentsList];
      setUpdatedUserList(modifiedList);
    }
  }, [userAgentsList]);

  useEffect(() => {
    if (!drawerStatus) {
      resetStates();
    } else {
      loadContactInGroup();
      apiCallGetUserAgents();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [drawerStatus]);

  return (
    <>
      {isLoading && <CustomLoader />}
      <Card className="h-full w-full">
        <CardHeader floated={false} shadow={false} className="rounded-none">
          <div className="mt-2 mb-4 flex flex-col justify-between gap-8 md:flex-row md:items-center">
            <Typography variant="h5" color="blue-gray">
              Manage All Contacts
            </Typography>
            <div className="flex items-center gap-5 text-sm">
              <span>
                <span>Selected: </span>
                <span>{totalSelectedCount}</span>
              </span>
              |
              <span>
                <span>Max Contacts allowed in a Group: </span>
                <span>{currentUserInfo?.maxContactsInGroup}</span>
              </span>
              |
              <span>
                <span>Not interested contacts: </span>
                <span>{notInterestedContact}</span>
              </span>
            </div>
            <div className="flex w-full gap-5 md:w-max">
              <Badge
                color="error"
                variant="dot"
                sx={{
                  "& .MuiBadge-dot": {
                    height: "14px",
                    minWidth: "14px",
                    borderRadius: "7px",
                  },
                }}
                invisible={!hasFilter}
                overlap="circular"
              >
                <IconButton
                  title="Filter Contact"
                  className="bg-blue-600"
                  onClick={() => setShowFilter(!showFilter)}
                >
                  <FilterListIcon />
                </IconButton>
              </Badge>
              {isUserAdminOrSuperadmin() && (
                <IconButton
                  className="bg-green-600"
                  title="Download All Contacts"
                  onClick={() => downloadAllContactsOnClick()}
                >
                  <DownloadIcon />
                </IconButton>
              )}
              <button
                onClick={() => callbackHideAllContacts()}
                className="my-button-style-cancel right-5"
              >
                <IoClose size={18} />
              </button>
            </div>
          </div>
        </CardHeader>
        <CardBody
          onScroll={(e) => e.stopPropagation()}
          style={{ height: tableDialogSize }}
          className="p-0"
        >
          <Paper style={{ position: "relative", zIndex: 1, height: "75vh" }}>
            <DataGrid
              getRowId={(row) => row?.contactId}
              rows={filterContact}
              columns={contactsColumns}
              sx={{ border: 0 }}
              getRowClassName={() => "cursor-pointer"}
              disableRowSelectionOnClick={true}
              hideFooter={true}
              pagination={false}
            />
          </Paper>
        </CardBody>
        <CardFooter className="p-3 border-t border-blue-gray-70">
          <div className="w-full flex flex-wrap items-center gap-3">
            <div className="self-start z-50">
              <MTSelect
                variant="outlined"
                label="Page Size"
                color="blue"
                required
                value={contactPageSize}
                onChange={async (pageSize) => {
                  setContactPageSize(pageSize);
                  const contact = await apiCallFetchUserContacts(
                    currentPage,
                    searchContact,
                    pageSize
                  );
                  setFilterContact(contact?.contacts);
                  apiCallFetchTablePaging(searchContact, pageSize);
                  setTotalSelectedCount(0);
                  setSelectAll(false);
                  setContactsSelected([]);
                }}
                className="z-50"
              >
                {pageSizeArray?.map((pageSize, index) => (
                  <Option
                    key={index}
                    value={pageSize}
                    className={`hover:bg-blue-300 focus:bg-blue-300`}
                  >
                    {pageSize}
                  </Option>
                ))}
              </MTSelect>
            </div>
            <div className="flex space-x-2 self-end">
              <button
                onClick={handlePrevious}
                disabled={currentPage === 1}
                className={`px-2 py-1 rounded ${
                  currentPage === 1 ? "bg-gray-300" : "bg-blue-500 text-white"
                }`}
              >
                Previous
              </button>
              {getVisiblePages().map((page) => (
                <button
                  key={page}
                  onClick={() => getContactsOnClick(page)}
                  className={`px-2 py-1 rounded ${
                    currentPage === page
                      ? "bg-blue-500 text-white"
                      : "bg-gray-300"
                  }`}
                >
                  {page}
                </button>
              ))}
              <button
                onClick={handleNext}
                disabled={currentPage === totalPages.length}
                className={`px-2 py-1 rounded ${
                  currentPage === totalPages.length
                    ? "bg-gray-300"
                    : "bg-blue-500 text-white"
                }`}
              >
                Next
              </button>
            </div>

            <div className="lg:mx-10 text-slate-400">|</div>

            {contactsSelected?.length > 0 && (
              <form
                onSubmit={assignContactGroupsOnSubmit}
                className="flex flex-row md:flex-nowrap items-center gap-3"
              >
                <MTSelect
                  variant="outlined"
                  label="Select Contact Group"
                  color="blue"
                  required
                  value={targetContactGroup}
                  onChange={(e) => setTargetContactGroup(e)}
                >
                  {contactGroups?.map((group, index) => (
                    <Option
                      key={index}
                      value={group?.groupName}
                      className={`hover:bg-blue-300 focus:bg-blue-300 text-sm`}
                    >
                      {group?.groupName} (
                      {group?.numberOfContacts ? group.numberOfContacts : 0}{" "}
                      contacts)
                    </Option>
                  ))}
                </MTSelect>

                <div className="flex flex-row items-center gap-2">
                  {targetContactGroup !== "" && (
                    <Button
                      type="submit"
                      variant="gradient"
                      size="sm"
                      color="green"
                    >
                      <span className="whitespace-nowrap">Add to Group</span>
                    </Button>
                  )}

                  <Button
                    type="button"
                    variant="filled"
                    color="blue"
                    title="Create new group"
                    size="sm"
                    onClick={() => {
                      setOpenGroupCreationDialog(true);
                    }}
                    className="whitespace-nowrap"
                  >
                    Create Group
                  </Button>
                  <Button
                    type="button"
                    variant="filled"
                    color="red"
                    title="Delete selected contact"
                    size="sm"
                    onClick={() => {
                      setOpenDeleteDialog(true);
                    }}
                    className="whitespace-nowrap"
                  >
                    Delete contact
                  </Button>
                  <Button
                    type="button"
                    variant="filled"
                    color="red"
                    size="sm"
                    onClick={() => {
                      setTargetContactGroup("");
                      setTotalSelectedCount(0);
                      setContactsSelected(() => []);
                    }}
                    className="whitespace-nowrap"
                  >
                    Cancel
                  </Button>
                </div>
              </form>
            )}
          </div>
        </CardFooter>
      </Card>

      <Dialog
        open={openEditContact}
        handler={() => setOpenEditContact(!openEditContact)}
        size="xl"
      >
        <span className="flex justify-between gap-3">
          <DialogHeader>
            Edit contact{" "}
            {`+${toEditContact?.countryCode} ${toEditContact?.phoneNo}`}
          </DialogHeader>
        </span>
        <form onSubmit={handleContactEditOnSubmit}>
          <DialogBody className="space-y-3">
            <div className="grid grid-cols-1 lg:grid-cols-3 xl:grid-cols-4 gap-3">
              {Object.entries(toEditContact).map(([key, value]) =>
                [
                  "createdBy",
                  "isVerifiedNumber",
                  "updatedBy",
                  "lastContactedBy",
                ]?.includes(key) ? (
                  <div
                    key={key}
                    className="bg-blue-50 p-2 space-y-1 rounded-md"
                  >
                    <p className="text-xs">{key}</p>
                    <p>{value === 1 ? "YES" : value === 0 ? "NO" : value}</p>
                  </div>
                ) : ["waOptIn", "isBlocked"]?.includes(key) ? (
                  <div
                    key={key}
                    className="px-2 py-1 flex items-center gap-1 rounded-md border border-slate-200"
                  >
                    <p className="text-xs">{key}</p>
                    <select
                      name={key}
                      value={value}
                      onChange={handleContactEditOnChange}
                      className="wbm-select-1"
                    >
                      <option value="2" hidden ng-disabled="!!model">
                        ~ Not Set ~
                      </option>
                      <option key="0" value="0">
                        No
                      </option>
                      <option key="1" value="1">
                        Yes
                      </option>
                    </select>
                  </div>
                ) : ["lastContacted"]?.includes(key) ? (
                  <div
                    key={key}
                    className="bg-blue-50 p-2 space-y-1 rounded-md"
                  >
                    <p className="text-xs">{key}</p>
                    <p>{value ? isoTimestampToLocal(value) : "-"}</p>
                  </div>
                ) : (
                  !["contactData", "contactId"]?.includes(key) &&
                  key !== "indx" && (
                    <div key={key}>
                      <Input
                        name={key}
                        label={key}
                        color="blue"
                        value={value || ""}
                        onChange={handleContactEditOnChange}
                      />
                    </div>
                  )
                )
              )}
            </div>
            {toEditContact?.contactData &&
              Object.keys(toEditContact.contactData).length > 0 && (
                <div className="h-64 mt-4 p-3 bg-blue-50 rounded-md border border-blue-200">
                  <h4 className="text-sm font-semibold mb-2 text-blue-800">
                    Contact Data
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                    {Object.entries(toEditContact.contactData).map(
                      ([key, value]) => (
                        <div key={key} className="flex gap-2">
                          <p>{key} : </p>
                          <p>{value || ""}</p>
                        </div>
                      )
                    )}
                  </div>
                </div>
              )}
          </DialogBody>
          <DialogFooter>
            <Button
              type="button"
              variant="text"
              color="red"
              size="sm"
              onClick={() => setOpenEditContact(!openEditContact)}
              className="mr-1"
            >
              Cancel
            </Button>
            <Button
              className="mr-1"
              variant="gradient"
              color="red"
              size="sm"
              onClick={(e) => {
                deleteSelectedContactsOnClick(e);
              }}
            >
              Delete
            </Button>
            <Button type="submit" variant="gradient" color="green" size="sm">
              Save Edits
            </Button>
          </DialogFooter>
        </form>
      </Dialog>

      <Dialog
        open={showFilter}
        handler={() => setShowFilter(!showFilter)}
        className="w-36"
      >
        <DialogHeader className="flex items-center gap-2 text-blue-600 text-xl">
          <FilterListIcon className="text-blue-600" />
          <Typography variant="h5" className="font-semibold">
            Filter Contact
          </Typography>
        </DialogHeader>
        <DialogBody className="space-y-3">
          <div className="flex flex-col justify-start">
            <div className="flex flex-row justify-evenly gap-2">
              <FormControl fullWidth margin="normal" className="mt-0 mb-1">
                <MTSelect
                  value={searchContact?.isVerifiedNo.toString()}
                  onChange={handleChange("isVerifiedNo")}
                  label="Is Verified"
                >
                  <Option
                    value="-1"
                    className={`hover:bg-blue-300 focus:bg-blue-300`}
                  >
                    All
                  </Option>
                  <Option
                    value="1"
                    className={`hover:bg-blue-300 focus:bg-blue-300`}
                  >
                    Yes
                  </Option>
                  <Option
                    value="0"
                    className={`hover:bg-blue-300 focus:bg-blue-300`}
                  >
                    No
                  </Option>
                </MTSelect>
              </FormControl>

              <FormControl fullWidth margin="normal" className="mt-0 mb-1">
                <MTSelect
                  value={searchContact?.waOptIn.toString()}
                  onChange={handleChange("waOptIn")}
                  label="WA Opt In"
                >
                  <Option
                    value="-1"
                    className={`hover:bg-blue-300 focus:bg-blue-300`}
                  >
                    All
                  </Option>
                  <Option
                    value="1"
                    className={`hover:bg-blue-300 focus:bg-blue-300`}
                  >
                    Yes
                  </Option>
                  <Option
                    value="0"
                    className={`hover:bg-blue-300 focus:bg-blue-300`}
                  >
                    No
                  </Option>
                </MTSelect>
              </FormControl>
              <FormControl fullWidth margin="normal" className="mt-0 mb-1">
                <MTSelect
                  value={searchContact?.isBlocked.toString()}
                  onChange={handleChange("isBlocked")}
                  label="Is Blocked"
                >
                  <Option
                    value="-1"
                    className={`hover:bg-blue-300 focus:bg-blue-300`}
                  >
                    All
                  </Option>
                  <Option
                    value="1"
                    className={`hover:bg-blue-300 focus:bg-blue-300`}
                  >
                    Yes
                  </Option>
                  <Option
                    value="0"
                    className={`hover:bg-blue-300 focus:bg-blue-300`}
                  >
                    No
                  </Option>
                </MTSelect>
              </FormControl>
            </div>
            <TextField
              fullWidth
              label="Name"
              variant="outlined"
              size="small"
              sx={{ mt: 0, mb: 1 }}
              value={searchContact?.name}
              onChange={handleChange("name")}
            />
            <TextField
              fullWidth
              label="Phone Number"
              variant="outlined"
              size="small"
              sx={{ mt: 0, mb: 1 }}
              value={searchContact?.phoneNumber}
              onChange={handleChange("phoneNumber")}
            />
            <div className="flex flex-row justify-between gap-2">
              <FormControl fullWidth className="mt-0 mb-1">
                <MTSelect
                  value={searchContact?.createdBy ?? "all"}
                  onChange={handleChange("createdBy")}
                  className="h-[40px]"
                  label="Created By"
                >
                  {updatedUserList.map((item) => (
                    <Option
                      className="hover:bg-blue-300 focus:bg-blue-300"
                      key={item.agentUsername}
                      value={item.agentUsername}
                    >
                      {item.agentUsername === "all"
                        ? "All"
                        : item.agentUsername}
                    </Option>
                  ))}
                </MTSelect>
              </FormControl>
              <FormControl fullWidth className="mt-0 mb-1">
                <MTSelect
                  value={searchContact?.lastContactedBy ?? "all"}
                  onChange={handleChange("lastContactedBy")}
                  className="h-[40px]"
                  label="Last Contacted By"
                >
                  {updatedUserList.map((item) => (
                    <Option
                      className="hover:bg-blue-300 focus:bg-blue-300"
                      key={item.agentUsername}
                      value={item.agentUsername}
                    >
                      {item.agentUsername === "all"
                        ? "All"
                        : item.agentUsername}
                    </Option>
                  ))}
                </MTSelect>
              </FormControl>
            </div>
            <div className="mt-5 flex flex-row justify-between gap-2">
              <Input
                variant="outlined"
                color="green"
                type="date"
                label="Last Contacted On"
                name="dateLastContacted"
                containerProps={{
                  className: "min-w-0 h-[40px]",
                }}
                value={searchContact.dateLastContacted}
                onChange={handleDateChange}
              />
              <Input
                variant="outlined"
                color="green"
                type="date"
                name="createdOn"
                label="Created On"
                containerProps={{
                  className: "min-w-0 h-[40px]",
                }}
                value={searchContact.createdOn}
                onChange={handleDateChange}
              />
            </div>
            <div className="mt-4 flex flex-row justify-end gap-2">
              <Button
                color="secondary"
                variant="outlined"
                onClick={() => {
                  setSearchContact(defaultSearchData);
                }}
              >
                Clear Filter
              </Button>
              <Button
                color="blue"
                variant="contained"
                onClick={(e) => {
                  getContactsOnSubmit(e);
                  setShowFilter(false);
                }}
              >
                Apply
              </Button>
              <Button
                color="red"
                variant="contained"
                onClick={() => setShowFilter(!showFilter)}
              >
                Cancel
              </Button>
            </div>
          </div>
        </DialogBody>
      </Dialog>
      <Dialog
        open={openGroupCreationDialog}
        handler={() => setOpenGroupCreationDialog(!openGroupCreationDialog)}
        className=""
      >
        <span className="flex justify-between gap-3">
          <DialogHeader>Add Contact Group</DialogHeader>
        </span>
        <DialogBody>
          <CreateContactGroup
            callbackHideContactGroup={() => {
              apiCallFetchContactGroups();
              setOpenGroupCreationDialog(!openGroupCreationDialog);
            }}
          />
        </DialogBody>
      </Dialog>
      <Dialog
        className="lg:min-w-[30%] lg:w-[30%]"
        open={openDeleteDialog}
        handler={() => setOpenDeleteDialog(!openDeleteDialog)}
      >
        <DialogBody>
          <div className="text-red-700">
            Do you want to delete the selected contacts?
          </div>
        </DialogBody>

        <DialogFooter className="flex flex-row justify-end gap-2 ">
          <Button
            type="button"
            variant="filled"
            color="green"
            size="sm"
            onClick={handleSelectedContactDelete}
          >
            Confirm
          </Button>
          <Button
            type="button"
            variant="filled"
            color="red"
            size="sm"
            onClick={() => {
              setOpenDeleteDialog(false);
            }}
          >
            Cancel
          </Button>
        </DialogFooter>
      </Dialog>
    </>
  );
};

export default ManageAllContacts;
